/**
 * Helpers purs pour manipuler les blocs de leçon V2 côté éditeur.
 */

import type { LessonBlock, LessonBlockSection, LessonBlockType } from './types';

/** Renvoie le champ texte principal éditable d'un bloc (selon son type). */
export function getPrimaryTextField(blockType: LessonBlockType): 'content_md' | 'text' | 'body_md' {
  switch (blockType) {
    case 'heading':
      return 'text';
    case 'text':
    case 'key_point':
    case 'example':
      return 'content_md';
    case 'note':
      return 'body_md';
  }
}

/** Filtre les blocs d'une section donnée, triés par position. */
export function blocksInSection(blocks: LessonBlock[], section: LessonBlockSection): LessonBlock[] {
  return blocks.filter((b) => b.section === section).sort((a, b) => a.position - b.position);
}

/** Calcule une position pour insérer après le bloc d'index `i` dans une liste triée. */
export function positionAfter(sectionBlocks: LessonBlock[], indexAfter: number): number {
  const cur = sectionBlocks[indexAfter];
  const next = sectionBlocks[indexAfter + 1];
  if (!cur) return 1;
  if (!next) return cur.position + 1;
  return (cur.position + next.position) / 2;
}

/** Calcule une position pour insérer avant le bloc d'index `i`. */
export function positionBefore(sectionBlocks: LessonBlock[], indexBefore: number): number {
  const cur = sectionBlocks[indexBefore];
  const prev = sectionBlocks[indexBefore - 1];
  if (!cur) return 1;
  if (!prev) return cur.position / 2;
  return (prev.position + cur.position) / 2;
}

/** Position d'append à la fin d'une section. */
export function positionAtEnd(sectionBlocks: LessonBlock[]): number {
  if (sectionBlocks.length === 0) return 1;
  return sectionBlocks[sectionBlocks.length - 1]!.position + 1;
}

/** Section attendue pour un type de bloc donné. */
export function sectionForType(blockType: LessonBlockType): LessonBlockSection {
  return blockType === 'note' ? 'meta' : 'content';
}

/** Bloc « brut » issu du parsing markdown, prêt à être inséré. */
export interface ParsedLessonBlock {
  block_type: LessonBlockType;
  section: LessonBlockSection;
  data: Record<string, unknown>;
}

/**
 * Découpe un contenu markdown monolithique en blocs de leçon, en suivant la
 * même logique que le backfill SQL (migration 0103) :
 *   - une ligne `^#{1,6}\s` (avec texte non vide) → bloc `heading`
 *   - le texte entre deux titres → bloc `text`
 *   - le préambule avant le 1er titre → bloc `text`
 * Les niveaux de titre sont bornés à 3.
 *
 * ⚠️ Les lignes situées dans un bloc de code clôturé (``` ou ~~~) ne sont
 * JAMAIS traitées comme des titres : un commentaire Python `# affiche : 42`
 * serait sinon promu en `heading`, ce qui coupe le bloc de code en deux et
 * laisse un fence non fermé (contenu cassé à la vue élève).
 */
export function parseMarkdownToBlocks(contentMd: string): ParsedLessonBlock[] {
  const out: ParsedLessonBlock[] = [];
  const lines = (contentMd ?? '').split('\n');
  let buffer: string[] = [];

  const flush = () => {
    const text = buffer.join('\n').trim();
    if (text.length > 0) {
      out.push({ block_type: 'text', section: 'content', data: { content_md: text } });
    }
    buffer = [];
  };

  const headingRe = /^(#{1,6})\s+(.*)$/;
  const fenceRe = /^\s*(```|~~~)/;
  let inFence = false;
  for (const line of lines) {
    if (fenceRe.test(line)) {
      inFence = !inFence;
      buffer.push(line);
      continue;
    }
    const m = inFence ? null : headingRe.exec(line);
    const headingText = m ? m[2]!.trim() : '';
    if (m && headingText.length > 0) {
      flush();
      const level = Math.min(m[1]!.length, 3);
      out.push({
        block_type: 'heading',
        section: 'content',
        data: { text: headingText, level },
      });
    } else {
      buffer.push(line);
    }
  }
  flush();
  return out;
}
